// Centralized translations dictionary (EN + ID)
export const translations = {
  en: {
    nav: {
      home: "Home",
      rooms: "Rooms",
      restaurant: "Restaurant",
      laundry: "Laundry",
      travel: "Travel",
      faq: "FAQ",
      contact: "Contact",
      book: "Book on WhatsApp",
    },
    common: {
      from: "from",
      perNight: "/ night",
      perMonth: "/ month",
      perKg: "/ kg",
      perPair: "/ pair",
      learnMore: "Learn more",
      bookNow: "Book on WhatsApp",
      orderNow: "Order on WhatsApp",
      viewMenu: "View menu",
      viewRooms: "View rooms",
      readMore: "Read more",
      approxUSD: "approx.",
      liveRate: "Live USD/IDR",
    },
    welcome: {
      eyebrow: "Welcome to Agung Wiwin Homestay",
      title: "Comfort, Warmth & Your Best Bali Experience",
      p1: "Enjoy a comfortable atmosphere and the signature warmth of Balinese hospitality at Agung Wiwin Homestay, nestled in the heart of Pererenan, Canggu, Bali.",
      p2: "We're more than just a place to stay — we offer a complete range of services to support everything you need in Bali. From comfortable rooms, delicious meals at The Garden Restaurant, our Agung Wiwin Laundry service, to travel and transport arrangements to help you explore the beauty of the island.",
      p3: "Whether for a short holiday, long-term stay, or a getaway with family and friends, we're committed to providing warm service, true comfort, and a memorable experience.",
      tagline: "Stay, Dine, Explore & Relax — All in One Place.",
      closing: "Make Agung Wiwin Homestay part of your beautiful Bali story.",
    },
    hero: {
      eyebrow: "Pererenan · Bali",
      title: "Stay, Dine & Relax in Tropical Bali",
      subtitle:
        "Experience peaceful garden living, delicious homemade food, and convenient services in the heart of Pererenan, Bali.",
      ctaBook: "Book your stay",
      ctaMenu: "View restaurant menu",
      ctaLaundry: "Laundry service",
      ctaContact: "Contact us",
    },
    about: {
      eyebrow: "Welcome",
      title: "A boutique homestay surrounded by tropical gardens",
      p1: "Nestled in the peaceful village of Pererenan, Bali, Agung Wiwin Homestay offers a comfortable and relaxing stay surrounded by lush tropical gardens.",
      p2: "Whether you are visiting Bali for a holiday, remote work, or a long-term stay, our homestay provides the perfect balance between comfort, convenience, and authentic Balinese hospitality.",
      p3: "Guests can enjoy comfortable rooms, a large swimming pool, beautiful gardens, The Garden Cafe, laundry services, and easy access to nearby beaches.",
    },
    why: {
      eyebrow: "Why choose us",
      title: "Crafted for slow stays & sunlit mornings",
      items: [
        { t: "Tropical Garden Environment", d: "Relax in a peaceful atmosphere surrounded by tropical greenery." },
        { t: "Large Swimming Pool", d: "Enjoy our refreshing outdoor pool framed by frangipani and palms." },
        { t: "Fast WiFi 200 Mbps", d: "High-speed internet powered by GlobalXtreme — perfect for remote work." },
        { t: "Near the Beach", d: "About 10 minutes walking to Pererenan Beach and Lima Beach." },
        { t: "Restaurant On-Site", d: "Breakfast, Indonesian specialties, western dishes, coffee, juices, and bowls." },
        { t: "Laundry Service", d: "Fast and reliable laundry with pickup and delivery available." },
      ],
    },
    rooms: {
      eyebrow: "Stays",
      title: "Rooms designed for tropical living",
      subtitle:
        "Five thoughtfully appointed rooms across garden and pool views — perfect for short escapes and long stays.",
      facilities: "Facilities",
      capacity: "Capacity",
      bedSize: "Bed size",
      seasonLabel: "Season",
      breakfastLabel: "Breakfast",
      withBf: "With breakfast",
      noBf: "Room only",
      daily: "Daily",
      monthly: "Monthly",
      perMonthNote: "/ month · breakfast not included",
      lowSeason: "Low Season",
      highSeason: "High Season",
      peakSeason: "Peak Season",
      currentSeason: "Current season",
      bookRoom: "Book this room",
      viewDetails: "View details",
      note: "Monthly rate is for 30-night stays. Breakfast add-on: Rp 50.000 per room per night.",
    },
    restaurant: {
      eyebrow: "The Garden Cafe",
      title: "Breakfast, bowls & Balinese comfort food",
      subtitle:
        "Freshly prepared breakfast, Indonesian comfort food, western favorites, tropical smoothie bowls, fresh juices, coffee, and refreshing beverages in a peaceful garden setting.",
      hoursBreakfast: "Breakfast 08:00 – 10:30",
      hoursMain: "Main Menu 10:30 – 21:00",
      includedNote: "All breakfast includes coffee or tea and a fresh fruit plate.",
      categories: {
        breakfast: "Breakfast",
        indonesian: "Indonesian",
        western: "Western",
        snacks: "Snacks",
        desserts: "Desserts",
        coffee: "Coffee",
        nonCoffee: "Non-Coffee",
        juices: "Juices",
        bowls: "Smoothie Bowls",
      },
    },
    laundry: {
      eyebrow: "Agung Wiwin Laundry",
      title: "Cleaner Clothes · Brighter Days",
      slogan: "Clean Clothes, Happier You",
      subtitle: "Let us take care of your laundry while you enjoy your stay in Bali.",
      services: ["Standard 2-Day", "Express 6 Hours", "Express 3 Hours", "Ironing Only", "Shoe Cleaning", "Free Pickup & Delivery"],
      kiloTitle: "Price per kilogram",
      shoesTitle: "Shoe Cleaning",
      shoesSubtitle: "Per pair · fresh, sanitised, and ready to wear",
      pickupTitle: "Free Pickup & Delivery",
      pickupNote: "Free pickup & delivery within the Pererenan area.",
      finishingTitle: "Steam Iron Finishing",
      finishingNote: "We use a steam iron to provide a cleaner and neater finish on every piece.",
      caveatTitle: "Please note",
      caveatNote:
        "Some stains such as dried ink or dried blood may not be completely removed.",
      benefits: [
        "Fresh and clean",
        "More confidence",
        "Brighter days",
        "Fresh, neat, and fragrant",
      ],
      tagline: "Fresh, neat, and fragrant",
    },
    faq: {
      eyebrow: "FAQ",
      title: "Good things to know",
      items: [
        { q: "What time is check-in?", a: "Check-in starts at 2:00 PM." },
        { q: "What time is check-out?", a: "Check-out time is 11:00 AM." },
        { q: "Is WiFi included?", a: "Yes. High-speed WiFi up to 200 Mbps is included." },
        { q: "Is breakfast included?", a: "Selected room packages include breakfast." },
        { q: "How far is the beach?", a: "Approximately 10 minutes walking to Pererenan Beach and Lima Beach." },
        { q: "Do you offer monthly rentals?", a: "Yes, daily and monthly rentals are available." },
        { q: "Do you provide airport transfers?", a: "We can assist with transportation arrangements upon request." },
        { q: "Is laundry service available?", a: "Yes, standard and express laundry services are available." },
        {
          q: "What payment methods do you accept?",
          a: "Payments are accepted in Indonesian Rupiah (IDR). USD prices displayed are for comparison purposes only.",
        },
      ],
    },
    contact: {
      eyebrow: "Get in touch",
      title: "We'd love to host you",
      subtitle:
        "Reach us on WhatsApp for fastest response, or send a message and we'll get back to you shortly.",
      name: "Your name",
      email: "Email",
      phone: "Phone (optional)",
      subject: "Subject",
      message: "Message",
      submit: "Send message",
      whatsapp: "Chat on WhatsApp",
      success: "Thank you! Your message has been sent.",
      error: "Sorry, something went wrong. Please try WhatsApp.",
      address: "Pererenan, Canggu — Bali, Indonesia",
    },
    footer: {
      tagline: "Stay. Dine. Relax. — In the heart of Pererenan.",
      links: "Quick links",
      contact: "Contact",
      newsletter: "Receive seasonal stories & offers",
      rights: "All rights reserved.",
    },
  },
  id: {
    nav: {
      home: "Beranda",
      rooms: "Kamar",
      restaurant: "Restoran",
      laundry: "Laundry",
      travel: "Travel",
      faq: "FAQ",
      contact: "Kontak",
      book: "Pesan via WhatsApp",
    },
    common: {
      from: "mulai",
      perNight: "/ malam",
      perMonth: "/ bulan",
      perKg: "/ kg",
      perPair: "/ pasang",
      learnMore: "Selengkapnya",
      bookNow: "Pesan via WhatsApp",
      orderNow: "Pesan via WhatsApp",
      viewMenu: "Lihat menu",
      viewRooms: "Lihat kamar",
      readMore: "Selengkapnya",
      approxUSD: "sekitar",
      liveRate: "Kurs USD/IDR",
    },
    welcome: {
      eyebrow: "Selamat Datang di Agung Wiwin Homestay",
      title: "Kenyamanan, Kehangatan, dan Pengalaman Terbaik Anda di Bali",
      p1: "Nikmati suasana nyaman dan keramahan khas Bali bersama Agung Wiwin Homestay, yang berlokasi di kawasan Pererenan, Canggu, Bali.",
      p2: "Kami hadir bukan hanya sebagai tempat menginap, tetapi juga sebagai penyedia berbagai layanan untuk melengkapi kebutuhan Anda selama berada di Bali. Mulai dari penginapan yang nyaman, hidangan lezat di The Garden Restaurant, layanan Agung Wiwin Laundry, hingga layanan perjalanan dan transportasi untuk membantu Anda menjelajahi keindahan Pulau Bali.",
      p3: "Baik untuk liburan singkat, menginap jangka panjang, maupun perjalanan bersama keluarga dan teman, kami berkomitmen memberikan pelayanan yang ramah, kenyamanan, serta pengalaman menginap yang berkesan.",
      tagline: "Menginap, Bersantap, Menjelajah, dan Bersantai — Semua dalam Satu Tempat.",
      closing: "Jadikan Agung Wiwin Homestay bagian dari cerita indah perjalanan Anda di Bali.",
    },
    hero: {
      eyebrow: "Pererenan · Bali",
      title: "Menginap, Bersantap & Bersantai di Bali Tropis",
      subtitle:
        "Nikmati ketenangan taman, hidangan rumahan yang lezat, dan layanan praktis di jantung Pererenan, Bali.",
      ctaBook: "Pesan kamar",
      ctaMenu: "Lihat menu restoran",
      ctaLaundry: "Layanan laundry",
      ctaContact: "Hubungi kami",
    },
    about: {
      eyebrow: "Selamat datang",
      title: "Penginapan butik di tengah taman tropis",
      p1: "Terletak di desa Pererenan yang tenang, Agung Wiwin Homestay menawarkan tempat menginap yang nyaman dikelilingi taman tropis yang asri.",
      p2: "Baik untuk liburan, remote work, atau menginap jangka panjang, kami menghadirkan keseimbangan antara kenyamanan, kepraktisan, dan keramahan khas Bali.",
      p3: "Nikmati kamar nyaman, kolam renang besar, taman indah, The Garden Cafe, layanan laundry, dan akses mudah ke pantai terdekat.",
    },
    why: {
      eyebrow: "Mengapa kami",
      title: "Dirancang untuk menginap santai & pagi cerah",
      items: [
        { t: "Suasana Taman Tropis", d: "Bersantai di lingkungan yang damai dikelilingi tanaman tropis." },
        { t: "Kolam Renang Luas", d: "Nikmati kolam renang outdoor yang menyegarkan." },
        { t: "WiFi Cepat 200 Mbps", d: "Internet kencang dari GlobalXtreme, ideal untuk bekerja jarak jauh." },
        { t: "Dekat Pantai", d: "Sekitar 10 menit berjalan kaki ke Pantai Pererenan dan Pantai Lima." },
        { t: "Restoran di Tempat", d: "Sarapan, masakan Indonesia, hidangan barat, kopi, jus, dan smoothie bowl." },
        { t: "Layanan Laundry", d: "Cepat dan terpercaya dengan layanan antar-jemput tersedia." },
      ],
    },
    rooms: {
      eyebrow: "Akomodasi",
      title: "Kamar untuk hidup tropis",
      subtitle:
        "Lima pilihan kamar dengan pemandangan taman atau kolam — cocok untuk liburan singkat maupun jangka panjang.",
      facilities: "Fasilitas",
      capacity: "Kapasitas",
      bedSize: "Ukuran tempat tidur",
      seasonLabel: "Musim",
      breakfastLabel: "Sarapan",
      withBf: "Dengan sarapan",
      noBf: "Tanpa sarapan",
      daily: "Harian",
      monthly: "Bulanan",
      perMonthNote: "/ bulan · tidak termasuk sarapan",
      lowSeason: "Low Season",
      highSeason: "High Season",
      peakSeason: "Peak Season",
      currentSeason: "Musim saat ini",
      bookRoom: "Pesan kamar ini",
      viewDetails: "Lihat detail",
      note: "Harga bulanan untuk menginap 30 malam. Tambahan sarapan: Rp 50.000 per kamar per malam.",
    },
    restaurant: {
      eyebrow: "The Garden Cafe",
      title: "Sarapan, smoothie bowl & hidangan rumahan",
      subtitle:
        "Sarapan segar, masakan Indonesia, menu barat, smoothie bowl tropis, jus segar, kopi, dan minuman menyegarkan di taman yang damai.",
      hoursBreakfast: "Sarapan 08:00 – 10:30",
      hoursMain: "Menu Utama 10:30 – 21:00",
      includedNote: "Semua menu sarapan termasuk kopi atau teh dan piring buah segar.",
      categories: {
        breakfast: "Sarapan",
        indonesian: "Indonesia",
        western: "Barat",
        snacks: "Camilan",
        desserts: "Pencuci Mulut",
        coffee: "Kopi",
        nonCoffee: "Non-Kopi",
        juices: "Jus",
        bowls: "Smoothie Bowl",
      },
    },
    laundry: {
      eyebrow: "Agung Wiwin Laundry",
      title: "Pakaian Bersih · Hari Lebih Cerah",
      slogan: "Clean Clothes, Happier You",
      subtitle: "Biarkan kami mengurus pakaian Anda selama menikmati liburan di Bali.",
      services: ["Standar 2 Hari", "Express 6 Jam", "Express 3 Jam", "Setrika Saja", "Cuci Sepatu", "Gratis Antar Jemput"],
      kiloTitle: "Harga per kilogram",
      shoesTitle: "Cuci Sepatu",
      shoesSubtitle: "Per pasang · bersih, higienis, dan siap pakai",
      pickupTitle: "Gratis Antar Jemput",
      pickupNote: "Gratis antar jemput laundry di area Pererenan.",
      finishingTitle: "Setrika Uap",
      finishingNote: "Kami menggunakan setrika uap agar pakaian Anda lebih rapi dan bersih.",
      caveatTitle: "Catatan",
      caveatNote:
        "Beberapa noda seperti tinta atau noda darah yang sudah kering mungkin tidak hilang sepenuhnya.",
      benefits: [
        "Fresh and clean",
        "More confidence",
        "Brighter days",
        "Fresh, neat, and fragrant",
      ],
      tagline: "Fresh, neat, and fragrant",
    },
    faq: {
      eyebrow: "FAQ",
      title: "Hal yang perlu diketahui",
      items: [
        { q: "Jam check-in?", a: "Check-in mulai pukul 14:00." },
        { q: "Jam check-out?", a: "Check-out pukul 11:00." },
        { q: "Apakah WiFi termasuk?", a: "Ya. WiFi hingga 200 Mbps sudah termasuk." },
        { q: "Apakah sarapan termasuk?", a: "Paket kamar tertentu sudah termasuk sarapan." },
        { q: "Seberapa jauh pantai?", a: "Sekitar 10 menit jalan kaki ke Pantai Pererenan dan Pantai Lima." },
        { q: "Apakah ada sewa bulanan?", a: "Ya, tersedia sewa harian dan bulanan." },
        { q: "Apakah ada antar jemput bandara?", a: "Kami dapat membantu mengatur transportasi atas permintaan." },
        { q: "Apakah ada layanan laundry?", a: "Ya, tersedia laundry standar dan express." },
        {
          q: "Metode pembayaran apa yang diterima?",
          a: "Pembayaran diterima dalam Rupiah (IDR). Harga USD ditampilkan sebagai perbandingan saja.",
        },
      ],
    },
    contact: {
      eyebrow: "Hubungi kami",
      title: "Senang menyambut Anda",
      subtitle:
        "Hubungi kami via WhatsApp untuk respons tercepat, atau kirim pesan dan kami akan segera membalas.",
      name: "Nama",
      email: "Email",
      phone: "Telepon (opsional)",
      subject: "Subjek",
      message: "Pesan",
      submit: "Kirim pesan",
      whatsapp: "Chat via WhatsApp",
      success: "Terima kasih! Pesan Anda telah terkirim.",
      error: "Maaf, terjadi kesalahan. Silakan coba via WhatsApp.",
      address: "Pererenan, Canggu — Bali, Indonesia",
    },
    footer: {
      tagline: "Menginap. Bersantap. Bersantai. — Di jantung Pererenan.",
      links: "Tautan cepat",
      contact: "Kontak",
      newsletter: "Cerita musiman & penawaran",
      rights: "Hak cipta dilindungi.",
    },
  },
};
